import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, take, tap } from 'rxjs';
import Endpoints from '../endpoints';
import { LoginCredentialsDTO } from '../types/auth';
import {
  DISPLAY_NAME_HEADER_KEY,
  EMAIL_HEADER_KEY,
  ROLES_HEADER_KEY, toastrConfig,
  TOKEN_HEADER_KEY,
  USER_ID_HEADER_KEY, userRoles
} from '../../app.config';
import { map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

export class Authentication {
  isSuperAdmin: boolean;
  isOrganizationAdmin: boolean;
  isRegionalManager: boolean;
  isSiteManager: boolean;
  isViewer: boolean;

  constructor(public id: number,
              public email: string,
              public displayName: string,
              public roles: any) {
    this.isSuperAdmin = this.roles.includes(userRoles.superAdmin);
    this.isOrganizationAdmin = this.roles.includes(userRoles.organisationAdmin);
    this.isRegionalManager = this.roles.includes(userRoles.regionalManager);
    this.isSiteManager = this.roles.includes(userRoles.siteManager);
    this.isViewer = this.roles.includes(userRoles.viewer);
  }
}

@Injectable()
export class AuthService {
  private _authentication$ = new BehaviorSubject<Authentication | null>(null);

  constructor(private http: HttpClient,
              private toastr: ToastrService,
              private translateService: TranslateService) {
    this.loadAuthFromStorage();
  }

  /**
   * Ha az aktuális szerepkörök értékére van szükség valahol (pl. kérelem beküldés után) ezt kell használni.
   * A .is* metódusok lentebb az oldalbetöltéskor frissülnek, egyszer. A resolver-ek miatt kell rájuk a
   * take(1), ami miatt az érték változáskor nem emit-telnek.
   */
  get authentication$(): Observable<Authentication | null> {
    //return this._authentication$.asObservable().pipe(distinctUntilChanged(isEqual));
    return this._authentication$.asObservable();
  }

  private loadAuthFromStorage(): void {
    const token = localStorage.getItem(TOKEN_HEADER_KEY);
    const idStr = localStorage.getItem(USER_ID_HEADER_KEY);
    if (token && idStr) {
      const id = Number(idStr);
      const email = localStorage.getItem(EMAIL_HEADER_KEY) ?? '';
      const displayName = localStorage.getItem(DISPLAY_NAME_HEADER_KEY) ?? '';
      const roles = localStorage.getItem(ROLES_HEADER_KEY) ?? '';
      this._authentication$.next(new Authentication(id, email, displayName, [roles]));
    }
  }

  refreshAuthentication(response: HttpResponse<any>): void {
    if (!response.ok) {
      return;
    }

    const headers = response.headers;
    const token = headers.get(TOKEN_HEADER_KEY);
    if (!token) {
      return;
    }

    const id = Number(headers.get(USER_ID_HEADER_KEY) ?? '');
    const email = headers.get(EMAIL_HEADER_KEY) ?? '';
    const displayName = headers.get(DISPLAY_NAME_HEADER_KEY) ?? '';
    const roles= headers.get(ROLES_HEADER_KEY) ?? '';
    localStorage.setItem(USER_ID_HEADER_KEY, id.toString());
    localStorage.setItem(TOKEN_HEADER_KEY, token);
    localStorage.setItem(EMAIL_HEADER_KEY, email);
    localStorage.setItem(DISPLAY_NAME_HEADER_KEY, displayName);
    localStorage.setItem(ROLES_HEADER_KEY, roles);

    this._authentication$.next(new Authentication(id, email, displayName, [roles]));
  }

  login(credentials: LoginCredentialsDTO): Observable<any> {
    return this.http.post<any>(Endpoints.LOGIN, credentials, {observe: 'response'}).pipe(
      tap(response => this.refreshAuthentication(response))
    );
  }

  isLoggedIn(): Observable<boolean> {
    return this.authentication$.pipe(
      map(auth => auth !== null),
      take(1)
    );
  }

  logout(callback?: any) {
    this.http.post(Endpoints.LOGOUT, null).subscribe({
      next: (response: any) => {
        this.clearAuth();
        this.toastr.success(response.message, "", toastrConfig);
        if (callback) callback();
      },
      error: () => {
        this.toastr.error(this.translateService.instant("ADMIN.ORGANISATIONS.UPDATE_ERROR"), "", toastrConfig);
      },
    });
  }

  logoutOnError(response: HttpErrorResponse): void {
    if (response.status >= 500) {
      this.clearAuth();
    }
  }

  clearAuth(): void {
    this._authentication$.next(null);
    this.clearLocalStorage();
  }

  private clearLocalStorage() {
    localStorage.removeItem(TOKEN_HEADER_KEY);
    localStorage.removeItem(USER_ID_HEADER_KEY);
    localStorage.removeItem(EMAIL_HEADER_KEY);
    localStorage.removeItem(DISPLAY_NAME_HEADER_KEY);
    localStorage.removeItem(ROLES_HEADER_KEY);
  }

  isSuperAdmin(): Observable<boolean> {
    return this.authentication$.pipe(
      map(auth => auth ? auth.isSuperAdmin : false),
      take(1)
    );
  }

  isOrganisationAdmin(): Observable<boolean> {
    return this.authentication$.pipe(
      map(auth => auth ? auth.isOrganizationAdmin : false),
      take(1)
    );
  }

  isRegionalManager(): Observable<boolean> {
    return this.authentication$.pipe(
      map(auth => auth ? auth.isRegionalManager : false),
      take(1)
    );
  }

  isSiteManager(): Observable<boolean> {
    return this.authentication$.pipe(
      map(auth => auth ? auth.isSiteManager : false),
      take(1)
    );
  }

  isViewer(): Observable<boolean> {
    return this.authentication$.pipe(
      map(auth => auth ? auth.isViewer : false),
      take(1)
    );
  }

  /**
   * Visszaadja a bejelentkezett felhasználó elsődleges szerepkörét szinkron módon.
   */
  getCurrentUserRole(): string | null {
    const auth = this._authentication$.getValue();
    if (auth && auth.roles && auth.roles.length > 0) {
      return auth.roles[0];
    }
    return localStorage.getItem(ROLES_HEADER_KEY) ?? null;
  }

  /**
   * Visszaadja a bejelentkezett felhasználó elsődleges szerepkörét Observable-ként.
   */
  getCurrentUserRole$(): Observable<string | null> {
    return this.authentication$.pipe(
      map(auth => (auth && auth.roles && auth.roles.length > 0 ? auth.roles[0] : null)),
      take(1)
    );
  }
}
